import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartItems,
  selectCartTotal,
  clearCart,
} from "../redux/slice/CartSlice";
import { trackInitiateCheckout } from "../analytics/events";
import { getUTM } from "../analytics/events";
import config from "../config";
import noimage from "../image/noimage.jpg";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const initForm = {
  name: "", email: "", phone: "",
  address: "", city: "", state: "", pincode: "",
  payment_method: "cod", notes: "",
};

export default function Checkout() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const items     = useSelector(selectCartItems);
  const subtotal  = useSelector(selectCartTotal);
  const shipping  = items.length > 0 && subtotal < 999 ? 99 : 0;
  const total     = subtotal + shipping;

  const [form,       setForm]       = useState(initForm);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState("");

  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) navigate("/shop");
  }, [items, navigate]);

  // Fire InitiateCheckout once on mount
  useEffect(() => {
    if (items.length > 0) trackInitiateCheckout(items, total);
  }, []); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Name is required";
    if (!form.email.trim())   e.email   = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim())   e.phone   = "Phone is required";
    else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "Enter a valid 10-digit phone";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim())    e.city    = "City is required";
    if (!form.state)          e.state   = "State is required";
    if (!form.pincode.trim()) e.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(form.pincode)) e.pincode = "Enter a valid 6-digit pincode";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError("");

    const utm = getUTM();
    const payload = {
      ...form,
      items: items.map((i) => ({
        product_id: i.id,
        variant_id: i.variantId || undefined,
        qty:        i.qty,
      })),
      ...utm,
    };

    try {
      const res = await axios.post(`${config.apiUrl}/order`, payload);
      dispatch(clearCart());
      navigate(`/order-confirmation?order=${res.data.order_number}`);
    } catch (err) {
      const msg = err.response?.data?.message || "Something went wrong. Please try again.";
      setApiError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Checkout | MeraGym Shop</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      </HelmetProvider>

      <section className="mg-checkout-page">
        <div className="container">
          <nav className="pd-breadcrumb mb-3" aria-label="breadcrumb">
            <Link to="/shop">Shop</Link>
            <span>/</span>
            <Link to="/cart">Cart</Link>
            <span>/</span>
            <span>Checkout</span>
          </nav>

          <h1 className="mg-checkout-page-title">
            <i className="fas fa-lock me-2"></i>Secure Checkout
          </h1>

          <form onSubmit={handleSubmit} noValidate>
            <div className="row gy-4">
              {/* ── Left: Form ── */}
              <div className="col-lg-7">

                {/* Contact */}
                <div className="mg-checkout-section">
                  <h2 className="mg-checkout-section-title">
                    <span className="mg-step-num">1</span> Contact Information
                  </h2>
                  <div className="row gy-3">
                    <div className="col-12">
                      <label className="mg-form-label" htmlFor="name">Full Name *</label>
                      <input
                        id="name" name="name" type="text"
                        className={`mg-form-input${errors.name ? " is-invalid" : ""}`}
                        placeholder="e.g. Rahul Sharma"
                        value={form.name} onChange={handleChange}
                      />
                      {errors.name && <span className="mg-form-error">{errors.name}</span>}
                    </div>
                    <div className="col-md-6">
                      <label className="mg-form-label" htmlFor="email">Email Address *</label>
                      <input
                        id="email" name="email" type="email"
                        className={`mg-form-input${errors.email ? " is-invalid" : ""}`}
                        placeholder="rahul@example.com"
                        value={form.email} onChange={handleChange}
                      />
                      {errors.email && <span className="mg-form-error">{errors.email}</span>}
                    </div>
                    <div className="col-md-6">
                      <label className="mg-form-label" htmlFor="phone">Phone Number *</label>
                      <div className="mg-phone-input">
                        <span className="mg-phone-prefix">+91</span>
                        <input
                          id="phone" name="phone" type="tel"
                          className={`mg-form-input${errors.phone ? " is-invalid" : ""}`}
                          placeholder="9999999999"
                          value={form.phone} onChange={handleChange}
                          maxLength={10}
                        />
                      </div>
                      {errors.phone && <span className="mg-form-error">{errors.phone}</span>}
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="mg-checkout-section">
                  <h2 className="mg-checkout-section-title">
                    <span className="mg-step-num">2</span> Delivery Address
                  </h2>
                  <div className="row gy-3">
                    <div className="col-12">
                      <label className="mg-form-label" htmlFor="address">Address *</label>
                      <input
                        id="address" name="address" type="text"
                        className={`mg-form-input${errors.address ? " is-invalid" : ""}`}
                        placeholder="House/Flat No., Street, Landmark"
                        value={form.address} onChange={handleChange}
                      />
                      {errors.address && <span className="mg-form-error">{errors.address}</span>}
                    </div>
                    <div className="col-md-6">
                      <label className="mg-form-label" htmlFor="city">City *</label>
                      <input
                        id="city" name="city" type="text"
                        className={`mg-form-input${errors.city ? " is-invalid" : ""}`}
                        placeholder="e.g. Lucknow"
                        value={form.city} onChange={handleChange}
                      />
                      {errors.city && <span className="mg-form-error">{errors.city}</span>}
                    </div>
                    <div className="col-md-6">
                      <label className="mg-form-label" htmlFor="pincode">Pincode *</label>
                      <input
                        id="pincode" name="pincode" type="text"
                        className={`mg-form-input${errors.pincode ? " is-invalid" : ""}`}
                        placeholder="e.g. 226001"
                        value={form.pincode} onChange={handleChange}
                        maxLength={6}
                      />
                      {errors.pincode && <span className="mg-form-error">{errors.pincode}</span>}
                    </div>
                    <div className="col-12">
                      <label className="mg-form-label" htmlFor="state">State *</label>
                      <select
                        id="state" name="state"
                        className={`mg-form-input mg-form-select${errors.state ? " is-invalid" : ""}`}
                        value={form.state} onChange={handleChange}
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.state && <span className="mg-form-error">{errors.state}</span>}
                    </div>
                    <div className="col-12">
                      <label className="mg-form-label" htmlFor="notes">Order Notes (Optional)</label>
                      <textarea
                        id="notes" name="notes" rows={3}
                        className="mg-form-input"
                        placeholder="Any special instructions for your order..."
                        value={form.notes} onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mg-checkout-section">
                  <h2 className="mg-checkout-section-title">
                    <span className="mg-step-num">3</span> Payment Method
                  </h2>
                  <div className="mg-payment-options">
                    <label
                      className={`mg-payment-option${form.payment_method === "cod" ? " active" : ""}`}
                    >
                      <input
                        type="radio" name="payment_method" value="cod"
                        checked={form.payment_method === "cod"}
                        onChange={handleChange}
                      />
                      <i className="fas fa-money-bill-wave"></i>
                      <div>
                        <strong>Cash on Delivery</strong>
                        <span>Pay when your order arrives</span>
                      </div>
                    </label>
                    <label className="mg-payment-option mg-payment-disabled">
                      <input type="radio" disabled />
                      <i className="fas fa-credit-card"></i>
                      <div>
                        <strong>Online Payment</strong>
                        <span>Coming Soon — UPI, Cards, Net Banking</span>
                      </div>
                      <span className="mg-coming-soon-badge">Coming Soon</span>
                    </label>
                  </div>
                </div>

                {apiError && (
                  <div className="mg-api-error">
                    <i className="fas fa-exclamation-circle me-2"></i>{apiError}
                  </div>
                )}
              </div>

              {/* ── Right: Order Summary ── */}
              <div className="col-lg-5">
                <div className="mg-checkout-summary">
                  <h3 className="mg-summary-title">Order Summary</h3>

                  <div className="mg-checkout-items">
                    {items.map((item) => (
                      <div className="mg-checkout-item" key={item.key}>
                        <div className="mg-checkout-item-img">
                          <img
                            src={item.image || noimage}
                            alt={item.title}
                            onError={(e) => { e.target.src = noimage; }}
                          />
                          <span className="mg-checkout-item-qty">{item.qty}</span>
                        </div>
                        <div className="mg-checkout-item-meta">
                          <p>{item.title}</p>
                          {item.variantInfo && <span>{item.variantInfo}</span>}
                        </div>
                        <div className="mg-checkout-item-price">
                          ₹{(item.price * item.qty).toLocaleString("en-IN")}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mg-summary-rows mt-3">
                    <div className="mg-summary-row">
                      <span>Subtotal</span>
                      <span>₹{subtotal.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mg-summary-row">
                      <span>Shipping</span>
                      <span className={shipping === 0 ? "text-success fw-bold" : ""}>
                        {shipping === 0 ? "FREE" : `₹${shipping}`}
                      </span>
                    </div>
                  </div>

                  <div className="mg-summary-total">
                    <span>Total</span>
                    <span>₹{total.toLocaleString("en-IN")}</span>
                  </div>

                  <button
                    type="submit"
                    className="mg-place-order-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Placing Order...</>
                    ) : (
                      <><i className="fas fa-check-circle me-2"></i>Place Order — ₹{total.toLocaleString("en-IN")}</>
                    )}
                  </button>

                  <p className="mg-secure-note">
                    <i className="fas fa-lock me-1"></i>
                    Your information is secure and encrypted
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
